/* ASSET - assets/coco-sticky-atc.js */

/* Sticky add-to-cart bar - <coco-sticky-atc> lives outside the product
   section (rendered by layout/theme.liquid via snippets/coco-sticky-atc), so
   it survives Section Rendering API swaps and must find everything it needs
   at runtime. section.id is not reachable in the layout, so the real submit
   button is resolved from the DOM instead of by id.

   State sources, in the order they matter:
   - IntersectionObserver on the real button decides visibility: the bar
     shows only once the button has scrolled up out of view (bottom < 0),
     matching the mockup, not merely below the fold on page load.
   - MutationObserver on the real button mirrors disabled + label. This also
     covers VariantSelects.setUnavailable(), which never publishes an event.
     When Settings > Product Custom carries a bar label, the ENABLED label
     comes from that setting instead of the mirror; disabled and sold-out
     always keep mirroring, so the bar can never claim a product is buyable
     when the form says otherwise.
   - MutationObserver on product-info's data-coco-bespoke hides the bar in
     bespoke mode, where coco-product-info.css hides the real ATC stack.
   - PUB_SUB_EVENTS.variantChange updates price / secondary line / thumbnail.
     Price text is cloned out of the fetched section HTML so money formatting
     stays Liquid's job - the theme has no JS money formatter. */

class CocoStickyAtc extends HTMLElement {
  connectedCallback() {
    this.productInfo = document.querySelector('product-info')
    this.realButton = this.findRealButton()
    this.button = this.querySelector('[data-coco-satc-submit]')
    this.secondaryEl = this.querySelector('[data-coco-satc-secondary]')
    this.sepEl = this.querySelector('[data-coco-satc-sep]')
    this.priceEl = this.querySelector('[data-coco-satc-price]')
    this.thumbWrap = this.querySelector('[data-coco-satc-thumb]')

    if (!this.realButton || !this.button) {
      this.setAttribute('data-coco-satc-off', '')
      return
    }

    this.onClick = () => {
      if (!this.realButton || this.realButton.hasAttribute('disabled')) return
      this.realButton.click()
    }
    this.button.addEventListener('click', this.onClick)

    this.syncFromRealButton()
    this.syncBespoke()

    this.buttonObserver = new MutationObserver(() => this.syncFromRealButton())
    this.buttonObserver.observe(this.realButton, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      subtree: true,
      characterData: true
    })

    if (this.productInfo) {
      this.bespokeObserver = new MutationObserver(() => this.syncBespoke())
      this.bespokeObserver.observe(this.productInfo, {
        attributes: true,
        attributeFilter: ['data-coco-bespoke']
      })
    }

    this.viewObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1]
      if (!entry) return
      const passed = entry.isIntersecting === false && entry.boundingClientRect.bottom < 0
      this.classList.toggle('coco-satc--visible', passed)
    })
    this.viewObserver.observe(this.realButton)

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event))
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe()
    if (this.buttonObserver) this.buttonObserver.disconnect()
    if (this.bespokeObserver) this.bespokeObserver.disconnect()
    if (this.viewObserver) this.viewObserver.disconnect()
    if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick)
  }

  /* The PDP renders exactly one product form (snippets/buy-buttons.liquid);
     scope to <product-info> first so a quick-add form elsewhere can never be
     picked up, then fall back to the id prefix. */
  findRealButton() {
    const scope = this.productInfo || document
    return (
      scope.querySelector('.product-form__submit[name="add"]') ||
      document.querySelector('[id^="ProductSubmitButton-"]')
    )
  }

  /* The observer is not switched off when an override is set - it still runs
     on every mutation, the enabled branch just reads a different source. That
     matters going the other way: a variant swap from sold out back to
     available has to put the override BACK, and a handler that skipped
     writing would leave "Sold out" on the bar for good.

     The disabled branch keeps mirroring, which is also how the locale
     sold-out string arrives: global.js toggleAddButton() writes
     window.variantStrings.soldOut into the real button, and this copies it. */
  syncFromRealButton() {
    if (!this.realButton || !this.button) return

    const disabled = this.realButton.hasAttribute('disabled')
    this.button.disabled = disabled

    const override = (this.dataset.cocoSatcLabel || '').trim()
    if (!disabled && override) {
      this.button.textContent = override
      return
    }

    const label = (this.realButton.textContent || '').replace(/\s+/g, ' ').trim()
    if (label) this.button.textContent = label
  }

  syncBespoke() {
    const bespoke = this.productInfo && this.productInfo.getAttribute('data-coco-bespoke') === 'true'
    if (bespoke) this.setAttribute('data-coco-satc-off', '')
    else this.removeAttribute('data-coco-satc-off')
  }

  onVariantChange(event) {
    if (!event || !event.data || !event.data.variant) return
    const variant = event.data.variant

    if (this.secondaryEl && this.dataset.cocoSatcSecondaryMode === 'variant') {
      const title = variant.title || ''
      this.secondaryEl.textContent = title
      this.secondaryEl.hidden = title === ''
      if (this.sepEl) this.sepEl.hidden = title === ''
    }

    if (this.priceEl && event.data.html) this.syncPrice(event.data.html)

    if (this.thumbWrap && variant.featured_media && variant.featured_media.preview_image) {
      const img = this.thumbWrap.querySelector('img')
      const src = variant.featured_media.preview_image.src
      if (img && src) {
        const sep = src.indexOf('?') > -1 ? '&' : '?'
        img.removeAttribute('srcset')
        img.src = src + sep + 'width=144'
      }
    }
  }

  /* snippets/price.liquid marks the sale state on the .price root; the
     matching amount span holds the Liquid-formatted money string. */
  syncPrice(html) {
    const block = html.querySelector('[id^="price-"]')
    if (!block) return
    const root = block.querySelector('.price')
    const onSale = root && root.classList.contains('price--on-sale')
    const source = onSale
      ? block.querySelector('.price__sale .price-item--sale')
      : block.querySelector('.price__regular .price-item--regular')
    if (source && source.textContent.trim()) this.priceEl.textContent = source.textContent.trim()
  }
}

if (!customElements.get('coco-sticky-atc')) {
  customElements.define('coco-sticky-atc', CocoStickyAtc)
}
